import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import {
  DashboardContent,
  Dispatch,
  PoContent,
  ReportsContent,
  ReceiptContent,
  BillsContent,
  DispatchDetail,
  ReceiptDetails,
  BillsDetails,
  PoDetails,
  MixBox,
  CreateDispatch,
  FabricDispatchDetail,
  PackageBlpOverview,
  YarnDispatchDetail,
  DispatchBlpOverview,
  Error404,
  Login,
  Manufacturing,
  ManufacturingDetails,
  PackageDetail,
  LogsContent,
} from './components/pages';
import { APPS, ROUTES, LOCAL_STORAGE } from './constants';
import { getLocalStorageItem } from './utils';
import { Layout } from './components/common/Layout';
import ContainerThreadBoxEntry from './components/common/ContainerThreadBoxEntry';
import { LogDetails } from './components/pages/logs/LogDetails';

const VendorAuthGuard = ({ children, appTag }) => {
  const authToken = getLocalStorageItem('auth_token');
  const allowedApps = [APPS.DASHBOARD, ...getLocalStorageItem(LOCAL_STORAGE.ALLOWED_APP)];

  if (!authToken) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (appTag && !allowedApps.includes(appTag)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return children;
};

const router = createBrowserRouter([
  {
    path: ROUTES.LOGIN,
    element: <Login />,
  },
  {
    path: ROUTES.HOME,
    element: <Layout />,
    children: [
      {
        path: ROUTES.DASHBOARD,
        element: (
          <VendorAuthGuard>
            <DashboardContent />
          </VendorAuthGuard>
        ),
      },
      {
        path: ROUTES.PURCHASE_ORDER,
        element: (
          <VendorAuthGuard appTag={APPS.PURCHASE}>
            <PoContent />
          </VendorAuthGuard>
        ),
      },
      {
        path: `${ROUTES.PURCHASE_ORDER}${ROUTES.URL}`,
        element: (
          <VendorAuthGuard appTag={APPS.PURCHASE}>
            <PoDetails />
          </VendorAuthGuard>
        ),
      },
      {
        path: ROUTES.BILLS,
        element: (
          <VendorAuthGuard appTag={APPS.BILL}>
            <BillsContent />
          </VendorAuthGuard>
        ),
      },
      {
        path: `${ROUTES.BILLS}${ROUTES.URL}`,
        element: (
          <VendorAuthGuard appTag={APPS.BILL}>
            <BillsDetails />
          </VendorAuthGuard>
        ),
      },
      {
        path: ROUTES.RECEIPT,
        element: (
          <VendorAuthGuard appTag={APPS.RECEIPT}>
            <ReceiptContent />
          </VendorAuthGuard>
        ),
      },
      {
        path: `${ROUTES.RECEIPT}${ROUTES.URL}`,
        element: (
          <VendorAuthGuard appTag={APPS.RECEIPT}>
            <ReceiptDetails />
          </VendorAuthGuard>
        ),
      },
      {
        path: ROUTES.MANUFACTURING,
        element: (
          <VendorAuthGuard appTag={APPS.MFG}>
            <Manufacturing />
          </VendorAuthGuard>
        ),
      },
      {
        path: `${ROUTES.MANUFACTURING}${ROUTES.URL}`,
        element: (
          <VendorAuthGuard appTag={APPS.MFG}>
            <ManufacturingDetails />
          </VendorAuthGuard>
        ),
      },
      {
        path: ROUTES.PACKAGE,
        element: (
          <VendorAuthGuard appTag={APPS.PACK}>
            <PackageDetail />
          </VendorAuthGuard>
        ),
      },
      {
        path: ROUTES.PACKAGE_BLP_OVERVIEW,
        element: (
          <VendorAuthGuard appTag={APPS.PACK}>
            <PackageBlpOverview />
          </VendorAuthGuard>
        ),
      },
      {
        path: ROUTES.DISPATCH_BLP_OVERVIEW,
        element: (
          <VendorAuthGuard appTag={APPS.DISPATCH}>
            <DispatchBlpOverview />
          </VendorAuthGuard>
        ),
      },
      {
        path: ROUTES.ADD_MIX_BOX,
        element: (
          <VendorAuthGuard appTag={APPS.DISPATCH}>
            <MixBox />
          </VendorAuthGuard>
        ),
      },
      {
        path: ROUTES.DISPATCH,
        element: (
          <VendorAuthGuard appTag={APPS.DISPATCH}>
            <Dispatch />
          </VendorAuthGuard>
        ),
      },
      {
        path: ROUTES.CREATE_DISPATCH,
        element: (
          <VendorAuthGuard appTag={APPS.DISPATCH}>
            <CreateDispatch />
          </VendorAuthGuard>
        ),
      },
      {
        path: `${ROUTES.DISPATCH_DETAILS}${ROUTES.URL}`,
        element: (
          <VendorAuthGuard appTag={APPS.DISPATCH}>
            <DispatchDetail />
          </VendorAuthGuard>
        ),
      },
      {
        path: `${ROUTES.CONTAINER_THREAD_BOX_ENTRY}`,
        element: (
          <VendorAuthGuard appTag={APPS.DISPATCH}>
            <ContainerThreadBoxEntry />
          </VendorAuthGuard>
        ),
      },
      {
        path: `${ROUTES.FABRIC_DISPATCH_DETAIL}${ROUTES.URL}`,
        element: (
          <VendorAuthGuard appTag={APPS.DISPATCH}>
            <FabricDispatchDetail />
          </VendorAuthGuard>
        ),
      },
      {
        path: `${ROUTES.YARN_DISPATCH_DETAIL}${ROUTES.URL}`,
        element: (
          <VendorAuthGuard appTag={APPS.DISPATCH}>
            <YarnDispatchDetail />
          </VendorAuthGuard>
        ),
      },
      {
        path: ROUTES.REPORTS,
        element: (
          <VendorAuthGuard appTag={APPS.REPORT_NAME}>
            <ReportsContent />
          </VendorAuthGuard>
        ),
      },
      {
        path: ROUTES.LOGS,
        element: (
          <VendorAuthGuard appTag={APPS.LOGS}>
            <LogsContent />
          </VendorAuthGuard>
        ),
      },
      {
        path: `${ROUTES.LOG_DETAILS}${ROUTES.URL}`,
        element: (
          <VendorAuthGuard appTag={APPS.LOGS}>
            <LogDetails />
          </VendorAuthGuard>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <Error404 />,
  },
]);

export default function AppRouter() {
  return <RouterProvider router={router} />;
}
